import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconAnimals, IconChart, IconCheckCircle, IconDroplet } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useOpuSession } from '../contexts/opu';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { reportShell, reportOptions, downloadPdf } from '../../reports/utils/shell';
import { buildOpuReportBody, opuReportFilename } from '../utils/report';
import { useOpuTransferSessions } from '../../transfers/contexts/transfers';
import TransferReportModal from '../../transfers/forms/report-modal';
import DxReportModal from '../../pregnancies/forms/report-modal';
import { TIMELINE_STEPS, reachedStep } from '../utils/phase';
import { ClivagesPanel, TransfersDxPanel, PartosPanel } from './phase-panels';

import './styles/landing.css';

// Fases con informe directo desde la vista de OPU (T.E. y Dx). Ambas usan las
// sesiones TE de la OPU como fuente; cada una abre su propio modal.
const REPORTABLE = { te: true, dx: true };

const ROLE_LABEL = { lead_vet: 'Aspirador', assistant: 'Asistente', operator: 'Operario' };

function Stat({ icon, label, value, accent }) {
    return (
        <div className={`opu-land__stat ${accent ? 'opu-land__stat--accent' : ''}`}>
            <span className="opu-land__stat-icon">{icon}</span>
            <span className="opu-land__stat-body">
                <span className="opu-land__stat-value">{value}</span>
                <span className="opu-land__stat-label">{label}</span>
            </span>
        </div>
    );
}

export default function OpuLanding({ sessionId }) {
    // view=resolution: trae lo mismo que standard + el rollup (clivages_count) que
    // necesitamos para decidir editar vs registrar clivage desde el detalle.
    const { data: session, isLoading, error } = useOpuSession(sessionId, 'resolution');
    const { data: opuTeData } = useOpuTransferSessions(sessionId);
    const { mutateAsync: generate, isPending } = useGeneratePdf();
    const [showTeReport, setShowTeReport] = useState(false);
    const [showDxReport, setShowDxReport] = useState(false);

    if (isLoading) return <div className="opu-land__state">Cargando sesión…</div>;
    if (error || !session) return <div className="opu-land__state">Sesión no encontrada.</div>;

    const aspirations = (session.aspirations || []).filter(a => a.status !== 0);
    // ¿la OPU ya tiene clivages? → editar (precarga registros); si no → registrar.
    const hasClivages = (session.resolution?.rollup?.clivages_count || 0) > 0;
    const clivageHref = hasClivages
        ? `/opu/clivage/edit?session=${session.id}`
        : `/opu/clivage?session=${session.id}`;
    const sumTotal = aspirations.reduce((a, r) => a + (Number(r.total) || 0), 0);
    const sumViables = aspirations.reduce((a, r) => a + (Number(r.viables) || 0), 0);
    const rate = sumTotal ? Math.round((sumViables / sumTotal) * 100) : 0;
    const team = (session.staff || []).filter(s => s.status !== 0);

    // Fases del flujo reproductivo (mismo modelo que el timeline de la lista):
    // completadas hasta `reached`, el resto pendientes.
    const reached = reachedStep(session);
    const teSessions = opuTeData?.transfer_sessions || [];

    // Navegación por fase (ir / editar / registrar): cada fase enlaza a su tablero
    // para no perder la referencia del flujo (T.E./Dx tras el clivage). Las fases
    // de OPU abren en pestaña propia (workspaces); Dx vive en el shell admin.
    const phaseAction = (key) => {
        switch (key) {
            case 'opu':     return { label: 'Editar sesión',                  href: `/opu/edit?session=${session.id}`,     domain: 'aspirations', action: 'write', newTab: true };
            case 'clivage': return { label: hasClivages ? 'Editar' : 'Registrar', href: clivageHref,                        domain: 'clivages',    action: 'write', newTab: true };
            case 'te':      return { label: reached >= 2 ? 'Editar' : 'Registrar', href: `/opu/transfer?session=${session.id}`, domain: 'transfers', action: 'write', newTab: true };
            case 'dx':      return { label: 'Ir',                             href: `/admin/dx?opu=${session.id}`,          domain: 'pregnancies', action: 'read',  newTab: false };
            default:        return null;
        }
    };

    const handleDownload = async () => {
        try {
            const html = reportShell({ bodyHtml: buildOpuReportBody(session) });
            const blob = await generate({ html, filename: opuReportFilename(session), options: reportOptions() });
            downloadPdf(blob, opuReportFilename(session));
            toast.success('Informe descargado');
        } catch (err) {
            // 403 is already toasted by the global interceptor — don't double up.
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el informe');
        }
    };

    return (
        <div className="opu-land">
            <header className="opu-land__header">
                <div>
                    <Link to="/admin/opu" className="opu-land__back">Sesiones OPU</Link>
                    <h1 className="opu-land__title">{session.code}</h1>
                </div>
                <div className="opu-land__header-actions">
                    {/* La navegación por fase (editar/registrar/ir) vive en la tarjeta
                        "Fases del flujo" para no perder la referencia del flujo completo. */}
                    <Gate domain="births" action="read">
                        <Link to={`/admin/partos?opu=${session.id}`} className="opu-land__partos">Partos</Link>
                    </Gate>
                    <Gate domain="reports" action="generate">
                        <button type="button" className="opu-land__report" onClick={handleDownload} disabled={isPending}>
                            {isPending ? 'Generando…' : 'Descargar informe PDF'}
                        </button>
                    </Gate>
                </div>
            </header>

            <section className="opu-land__meta">
                <div className="opu-land__meta-item">
                    <span className="opu-land__meta-label">Finca</span>
                    <span className="opu-land__meta-value">{session.farm?.name || '—'}</span>
                </div>
                <div className="opu-land__meta-item">
                    <span className="opu-land__meta-label">Fecha</span>
                    <span className="opu-land__meta-value">{formatDateOnly(session.date) || '—'}</span>
                </div>
                <div className="opu-land__meta-item">
                    <span className="opu-land__meta-label">Equipo</span>
                    <span className="opu-land__meta-value">
                        {team.length ? team.map(s => `${s.user?.name} (${ROLE_LABEL[s.role] || s.role})`).join(', ') : '—'}
                    </span>
                </div>
            </section>

            <section className="opu-land__stats">
                <Stat icon={<IconAnimals />} label="Donantes" value={aspirations.length} />
                <Stat icon={<IconDroplet />} label="Óvocitos totales" value={sumTotal} />
                <Stat icon={<IconCheckCircle />} label="Viables" value={sumViables} />
                <Stat icon={<IconChart />} label="Tasa de viabilidad" value={`${rate}%`} accent />
            </section>

            <section className="opu-land__phases-card">
                <h2 className="opu-land__section-title">Fases del flujo</h2>
                <div className="opu-land__phases">
                    {TIMELINE_STEPS.map((s, i) => {
                        const done = i <= reached;
                        const report = REPORTABLE[s.key];
                        const action = phaseAction(s.key);
                        return (
                            <div key={s.key} className={`opu-land__phase ${done ? 'opu-land__phase--done' : 'opu-land__phase--pending'}`}>
                                <span className="opu-land__phase-icon">
                                    {done ? <IconCheckCircle /> : <span className="opu-land__phase-dot" aria-hidden="true" />}
                                </span>
                                <span className="opu-land__phase-body">
                                    <span className="opu-land__phase-label">{s.label}</span>
                                    <span className="opu-land__phase-state">{done ? 'Completada' : 'Pendiente'}</span>
                                </span>
                                <div className="opu-land__phase-actions">
                                    {action && (
                                        <Gate domain={action.domain} action={action.action}>
                                            {action.newTab ? (
                                                <a href={action.href} target="_blank" rel="noopener noreferrer" className="opu-land__phase-go">{action.label}</a>
                                            ) : (
                                                <Link to={action.href} className="opu-land__phase-go">{action.label}</Link>
                                            )}
                                        </Gate>
                                    )}
                                    {report && (
                                        <Gate domain="reports" action="generate">
                                            {s.key === 'te' ? (
                                                <button
                                                    type="button"
                                                    className="opu-land__phase-report"
                                                    onClick={() => setShowTeReport(true)}
                                                    disabled={!done || !teSessions.length}
                                                    title={!done ? 'Disponible cuando haya transferencias' : (teSessions.length ? 'Imprimir informe de T.E.' : 'Cargando transferencias…')}
                                                >
                                                    Informe
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="opu-land__phase-report"
                                                    onClick={() => setShowDxReport(true)}
                                                    disabled={!teSessions.length}
                                                    title={teSessions.length ? 'Imprimir informe de Dx (estado + fechas estimadas)' : 'Disponible cuando haya transferencias'}
                                                >
                                                    Informe
                                                </button>
                                            )}
                                        </Gate>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="opu-land__table-card">
                <h2 className="opu-land__section-title">Aspiraciones</h2>
                {!aspirations.length ? (
                    <p className="opu-land__muted">Esta sesión no tiene aspiraciones.</p>
                ) : (
                    <table className="opu-land__table">
                        <thead>
                            <tr>
                                <th>#</th><th>Donante</th><th>Número</th>
                                <th>Óvocitos</th><th>Viables</th><th>% Viab.</th><th>Notas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aspirations.map((a, i) => {
                                const t = Number(a.total) || 0;
                                const v = Number(a.viables) || 0;
                                const r = t ? Math.round((v / t) * 100) : 0;
                                const notes = typeof a.remarks === 'string' ? a.remarks : '';
                                return (
                                    <tr key={a.id}>
                                        <td>{i + 1}</td>
                                        <td className="opu-land__td-name">{a.donor?.name || '—'}</td>
                                        <td>{a.donor?.number || '—'}</td>
                                        <td>{t}</td>
                                        <td>{v}</td>
                                        <td><span className={`opu-land__rate opu-land__rate--${r >= 70 ? 'good' : r >= 40 ? 'mid' : 'low'}`}>{r}%</span></td>
                                        <td className="opu-land__td-notes">{notes || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>

            <ClivagesPanel sessionId={session.id} aspirationIds={aspirations.map((a) => a.id)} />
            <TransfersDxPanel teSessions={teSessions} />
            <PartosPanel teSessions={teSessions} />

            {showTeReport && (
                <TransferReportModal
                    opuCode={session.code}
                    sessions={teSessions}
                    onClose={() => setShowTeReport(false)}
                />
            )}
            {showDxReport && (
                <DxReportModal
                    opuCode={session.code}
                    sessions={teSessions}
                    onClose={() => setShowDxReport(false)}
                />
            )}
        </div>
    );
}
